import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/format';
import styles from './star-rating.module.scss';

interface StarRatingProps {
  /** Already on the 0-5 scale. Use scoreToFive from @/utils/score first. */
  score: number | null;
  className?: string;
}

const STARS = [0, 1, 2, 3, 4];

export default function StarRating({ score, className }: StarRatingProps) {
  const { t } = useTranslation('metrics');

  // Non-finite folds in here too: NaN would otherwise round to NaN, leave every
  // star unfilled and render pixel-identical to a real zero rating.
  if (score === null || score === undefined || !Number.isFinite(score)) {
    return <span className={clsx(styles.none, className)}>{t('starRating.none')}</span>;
  }

  // Clamped as well as rounded. A caller wiring a raw 0-10 field straight in
  // would otherwise get five stars and an announced "8 out of 5".
  const bounded = Math.min(5, Math.max(0, score));
  const filled = Math.round(bounded);

  return (
    // role="img" so the label is the accessible name: on a bare span ARIA
    // prohibits naming, and the glyphs would be announced five times over.
    <span
      aria-label={t('starRating.label', { score: formatNumber(bounded) })}
      className={clsx(styles.stars, className)}
      role="img"
    >
      {STARS.map((index) => (
        <span
          aria-hidden="true"
          className={index < filled ? undefined : styles.empty}
          data-filled={index < filled}
          key={index}
        >
          ★
        </span>
      ))}
    </span>
  );
}
